/**
 * SOLID Review
 * S — Good. One job: tell the user when the backend is unreachable. The consecutive-failure threshold and the
 *     visibility-change reset serve that single job (avoiding false-positive flashes) and are `MaintenanceViewModel`'s since
 *     frontend-framework-oo P5 (§28.297).
 * O — Good. The model's `POLL_INTERVAL_MS` and `FAILURE_THRESHOLD` are named constants that tune behavior without touching logic.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Zero props — mounted once at the App root, self-contained.
 * D — Strong. The health check arrives as a prop (the Application's use case, from the composition root) and is handed to the
 *     model, which binds under the view-model provider alone — not the client core's. No store, no adapter.
 */
import React from 'react';
import { useViewModel } from '../../viewmodels/useViewModel';
import type { TCheckHealth } from '../../viewmodels/MaintenanceViewModel';

export interface IPropsMaintenanceBanner {
  /**
   * The Application's `checkServerHealth` (strict-ports FE5a: it was the probe itself, `healthAdapter`),
   * handed over by the composition root. A call that fails counts as a failed probe.
   */
  readonly checkHealth: TCheckHealth;
}

export const MaintenanceBanner: React.FC<IPropsMaintenanceBanner> = ({ checkHealth }) => {
  // The probe every 5 s, the three-failure threshold and the clean slate on resume are `MaintenanceViewModel`'s
  // (frontend-framework-oo P5, §28.297).
  const [{ down }] = useViewModel((views) => views.maintenance(), [checkHealth]);

  if (!down) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '8px 12px',
        backgroundColor: '#b45309',
        color: '#ffffff',
        fontFamily: 'system-ui, sans-serif',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.01em',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
      }}
    >
      <span aria-hidden="true">⚠️</span>
      Maintenance in progress — reconnecting…
    </div>
  );
};
